import type { AnalyticsMonth } from '@obras/shared';
import { formatCurrency, formatCurrencyCompact } from '@obras/shared';
import { useState } from 'react';

import { cn } from '../../lib/cn.js';

/** `2026-03` → `mar/26`. Eixo com o mês por extenso não cabe em doze colunas. */
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function rotulo(month: string): string {
  const [ano, mes] = month.split('-');
  const indice = Number(mes) - 1;
  return `${MESES[indice] ?? mes}/${(ano ?? '').slice(2)}`;
}

/**
 * Medições por mês.
 *
 * Uma série só, então não há legenda: o título já diz o que está plotado. E uma
 * série só é decisão de conteúdo, não de economia — aditivo e glosa mudam o
 * contrato, medição move dinheiro pago. Empilhar as três no mesmo eixo somaria
 * naturezas diferentes e inventaria uma grandeza que não existe; elas vivem nos
 * indicadores acima.
 *
 * O `pt-12` acima das barras é respiro para a dica de valor: sem ele, a dica da
 * barra mais alta sai do gráfico e cobre o botão "Ver tabela".
 */
export function MonthlyBars({ months }: { months: readonly AnalyticsMonth[] }) {
  const [hovered, setHovered] = useState<string | null>(null);
  const [asTable, setAsTable] = useState(false);

  const max = Math.max(...months.map((month) => month.measuredCents), 1);
  const peak = months.reduce<AnalyticsMonth | null>(
    (maior, mes) => (maior === null || mes.measuredCents > maior.measuredCents ? mes : maior),
    null,
  );

  if (months.length === 0) {
    return (
      <p className="px-5 pb-5 text-[13px] text-muted-foreground">
        Nenhuma movimentação no período escolhido.
      </p>
    );
  }

  return (
    <div className="px-5 pb-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="tabular text-[13px] text-muted-foreground">
          Maior mês: {peak ? `${rotulo(peak.month)} · ${formatCurrency(peak.measuredCents)}` : '—'}
        </p>
        {/* No papel o botão não leva a lugar nenhum. */}
        <button
          type="button"
          onClick={() => setAsTable((value) => !value)}
          className="text-[13px] text-muted-foreground transition-colors hover:text-foreground print:hidden"
        >
          {asTable ? 'Ver gráfico' : 'Ver tabela'}
        </button>
      </div>

      {asTable ? (
        <table className="tabular w-full text-[13px]">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="py-1.5 font-medium">Mês</th>
              <th className="py-1.5 text-right font-medium">Medido</th>
              <th className="py-1.5 text-right font-medium">Aditivos</th>
              <th className="py-1.5 text-right font-medium">Glosas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {months.map((month) => (
              <tr key={month.month}>
                <td className="py-1.5">{rotulo(month.month)}</td>
                <td className="py-1.5 text-right">{formatCurrency(month.measuredCents)}</td>
                <td className="py-1.5 text-right text-muted-foreground">
                  {formatCurrency(month.additionsCents)}
                </td>
                <td className="py-1.5 text-right text-muted-foreground">
                  {formatCurrency(month.glossCents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="relative pt-12">
          {/* Linhas de referência: filete sólido, um passo abaixo da superfície. */}
          <div className="pointer-events-none absolute inset-x-0 top-12 h-40">
            {[0, 0.5, 1].map((fracao) => (
              <div
                key={fracao}
                className="absolute inset-x-0 flex items-center gap-2"
                style={{ top: `${(1 - fracao) * 100}%` }}
              >
                <span className="tabular w-14 shrink-0 text-right text-[11px] text-muted-foreground">
                  {formatCurrencyCompact(max * fracao)}
                </span>
                <span className="h-px flex-1 bg-border" />
              </div>
            ))}
          </div>

          <div className="flex h-40 items-end gap-0.5 pl-16">
            {months.map((month) => {
              const altura = (month.measuredCents / max) * 100;
              const ativo = hovered === month.month;

              return (
                <div
                  key={month.month}
                  onMouseEnter={() => setHovered(month.month)}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered(month.month)}
                  onBlur={() => setHovered(null)}
                  tabIndex={0}
                  role="img"
                  aria-label={`${rotulo(month.month)}: ${formatCurrency(month.measuredCents)}`}
                  className="group relative flex h-full flex-1 cursor-default items-end justify-center rounded-sm"
                >
                  <span
                    className={cn(
                      'w-full max-w-6 rounded-t-[4px] transition-colors',
                      ativo ? 'bg-foreground' : 'bg-foreground/75',
                    )}
                    style={{ height: `${Math.max(altura, month.measuredCents > 0 ? 2 : 0)}%` }}
                  />

                  {ativo ? (
                    <span className="on-dark pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 rounded-sm bg-foreground px-2.5 py-1.5 text-[12px] whitespace-nowrap text-white">
                      <span className="block font-medium">{rotulo(month.month)}</span>
                      <span className="tabular block text-white/70">
                        {formatCurrency(month.measuredCents)}
                      </span>
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="mt-2 flex gap-0.5 pl-16">
            {months.map((month, index) => (
              <span
                key={month.month}
                className="tabular flex-1 text-center text-[11px] text-muted-foreground"
              >
                {/* Com muitos meses, rotula um sim outro não para não colidir. */}
                {months.length > 8 && index % 2 === 1 ? '' : rotulo(month.month)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
